import React, { useState } from 'react';
import { X, FolderOpen, Trash2, AlertTriangle } from 'lucide-react';
import { ProjectService } from '../../services/projectService';
import { GoogleDriveService } from '../../services/googleDrive';
import { DriveFolderSearchModal } from '../DriveIntegration';
import { Project, DriveFolder } from '../../types';

interface ProjectSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: Project;
  onProjectUpdated: (updatedProject: Project) => void;
  onProjectDeleted?: (projectId: string) => void;
}

export const ProjectSettingsModal: React.FC<ProjectSettingsModalProps> = ({
  isOpen,
  onClose,
  project,
  onProjectUpdated,
  onProjectDeleted
}) => {
  const [formData, setFormData] = useState({
    name: project.name,
    description: project.description,
    status: project.status
  });
  const [driveFolder, setDriveFolder] = useState<DriveFolder | null>(
    project.driveFolder ? {
      id: project.driveFolder.id,
      name: project.driveFolder.name,
      url: project.driveFolder.url
    } : null
  );
  const [driveFolderUrl, setDriveFolderUrl] = useState(project.driveFolder?.url || '');
  const [isDriveSearchOpen, setIsDriveSearchOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState('');

  const projectService = ProjectService.getInstance();
  const driveService = GoogleDriveService.getInstance();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDriveFolderSelect = (folder: DriveFolder) => {
    setDriveFolder(folder);
    setDriveFolderUrl(folder.url);
    setIsDriveSearchOpen(false);
  };

  const handleDriveUrlChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setDriveFolderUrl(url);
    
    if (url.trim() && driveService.isValidGoogleDriveUrl(url)) {
      try {
        const validationResult = await driveService.validateFolderUrl(url);
        if (validationResult.valid && validationResult.folder) {
          setDriveFolder(validationResult.folder);
        }
      } catch (error) {
        console.log('URL validation failed:', error);
        // Don't show error for invalid URLs during typing
      }
    } else if (!url.trim()) {
      setDriveFolder(null);
    }
  };

  const handleRemoveDriveFolder = () => {
    setDriveFolder(null);
    setDriveFolderUrl('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Project name is required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      console.log('🔄 Updating project settings...');
      
      const updates: Partial<Project> = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        status: formData.status as Project['status'],
        driveFolder: driveFolder || undefined
      };

      await projectService.updateProject(project.id, updates);
      
      const updatedProject = { ...project, ...updates };
      onProjectUpdated(updatedProject);
      
      console.log('✅ Project settings updated');
      onClose();
    } catch (err: any) {
      console.error('❌ Error updating project:', err);
      setError(`Failed to update project: ${err.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProject = async () => {
    setDeleting(true);
    setError('');

    try {
      console.log('🗑️ Deleting project...');
      await projectService.deleteProject(project.id);
      console.log('✅ Project deleted');
      
      if (onProjectDeleted) {
        onProjectDeleted(project.id);
      }
      onClose();
    } catch (err: any) {
      console.error('❌ Error deleting project:', err);
      setError(`Failed to delete project: ${err.message || 'Unknown error'}`);
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Project Settings</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Project Details */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter project name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Enter project description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>

            {/* Google Drive Integration */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700">
                  Google Drive Folder
                </label>
                <button
                  type="button"
                  onClick={() => setIsDriveSearchOpen(true)}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Search Drive
                </button>
              </div>

              {driveFolder ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <FolderOpen className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="font-medium text-green-800">{driveFolder.name}</p>
                        <p className="text-sm text-green-600 truncate">{driveFolder.url}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveDriveFolder}
                      className="text-red-600 hover:text-red-700 text-sm font-medium"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <input
                  type="url"
                  value={driveFolderUrl}
                  onChange={handleDriveUrlChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Paste Google Drive folder URL (optional)"
                />
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Project
              </button>

              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <AlertTriangle className="w-6 h-6 text-red-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Delete Project</h3>
              </div>
              
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete "{project.name}"? This action cannot be undone. 
                All tasks and project data will be permanently deleted.
              </p>
              
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteProject}
                  disabled={deleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleting ? 'Deleting...' : 'Delete Project'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Drive Folder Search Modal */}
      <DriveFolderSearchModal
        isOpen={isDriveSearchOpen}
        onClose={() => setIsDriveSearchOpen(false)}
        onFolderSelected={handleDriveFolderSelect}
      />
    </>
  );
};
